(() => {
  if (window.pictoMiniCarouselInstalled) return;
  window.pictoMiniCarouselInstalled = true;
  const instances = new Map();
  const mount = (scope = document) => {
    const roots = [...scope.querySelectorAll('[data-pm-carousel]')];
    if (scope.matches?.('[data-pm-carousel]')) roots.unshift(scope);
    roots.forEach(root => {
      if (instances.has(root)) return;
      const track = root.querySelector('[data-pm-carousel-track]');
      const controls = root.querySelector('[data-pm-carousel-controls]');
      if (!track || !controls) return;
      const cards = [...track.children];
      if (!cards.length) return;
      const prev = controls.querySelector('[data-pm-carousel-prev]');
      const next = controls.querySelector('[data-pm-carousel-next]');
      const position = controls.querySelector('[data-pm-carousel-position]');
      const abort = new AbortController();
      const on = (node, event, fn, opts = {}) => node.addEventListener(event, fn, { ...opts, signal: abort.signal });
      const rtl = () => getComputedStyle(track).direction === 'rtl';
      const behavior = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
      let frame = 0;
      let drag = null;
      let cancelClick = false;
      let clickTimer = 0;
      const update = () => {
        frame = 0;
        const r = track.getBoundingClientRect();
        const boxes = cards.map(c => c.getBoundingClientRect());
        const first = boxes[0], last = boxes.at(-1);
        const overflow = track.scrollWidth > track.clientWidth + 2;
        controls.hidden = !overflow;
        prev.disabled = rtl() ? first.right <= r.right + 2 : first.left >= r.left - 2;
        next.disabled = rtl() ? last.left >= r.left - 2 : last.right <= r.right + 2;
        const visible = boxes.map((b, i) => ({ i, fraction: Math.max(0, Math.min(b.right, r.right) - Math.max(b.left, r.left)) / Math.max(1, b.width) })).filter(b => b.fraction >= .5);
        const firstIndex = visible[0]?.i ?? 0;
        const lastIndex = visible.at(-1)?.i ?? firstIndex;
        const text = firstIndex === lastIndex ? `${firstIndex + 1} / ${cards.length}` : `${firstIndex + 1}–${lastIndex + 1} / ${cards.length}`;
        if (position.textContent !== text) position.textContent = text;
        boxes.forEach((b, i) => {
          const visibleWidth = Math.max(0, Math.min(b.right, r.right) - Math.max(b.left, r.left));
          if (visibleWidth < b.width * .5) cards[i].querySelectorAll('video').forEach(v => { if (!v.paused) v.pause(); });
        });
      };
      const requestUpdate = () => { if (!frame) frame = requestAnimationFrame(update); };
      const move = direction => {
        const rect = cards[0].getBoundingClientRect();
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        track.scrollBy({ left: direction * (rtl() ? -1 : 1) * (rect.width + gap), behavior: behavior() });
      };
      on(prev, 'click', () => move(-1));
      on(next, 'click', () => move(1));
      on(track, 'scroll', requestUpdate, { passive: true });
      on(track, 'keydown', event => {
        if (event.target !== track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          move((event.key === 'ArrowRight' ? 1 : -1) * (rtl() ? -1 : 1));
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          const card = event.key === 'Home' ? cards[0] : cards.at(-1);
          const box = card.getBoundingClientRect(), r = track.getBoundingClientRect();
          track.scrollBy({ left: rtl() ? box.right - r.right : box.left - r.left, behavior: behavior() });
        }
      });
      on(track, 'pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,a,input,select,textarea,video')) return;
        cancelClick = false;
        drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, moved: false };
      });
      on(track, 'pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        const delta = event.clientX - drag.x;
        if (!drag.moved && Math.abs(delta) < 6) return;
        if (!drag.moved) { drag.moved = true; track.setPointerCapture(event.pointerId); root.classList.add('pm-carousel-dragging'); }
        event.preventDefault();
        track.scrollLeft = drag.left - delta;
      });
      const endDrag = () => {
        if (!drag) return;
        cancelClick = drag.moved;
        const id = drag.id;
        drag = null;
        root.classList.remove('pm-carousel-dragging');
        if (track.hasPointerCapture(id)) track.releasePointerCapture(id);
        clearTimeout(clickTimer);
        clickTimer = setTimeout(() => { cancelClick = false; }, 0);
        requestUpdate();
      };
      on(track, 'pointerup', endDrag);
      on(track, 'pointercancel', endDrag);
      on(track, 'lostpointercapture', endDrag);
      on(track, 'pointerleave', () => { if (drag && !drag.moved) drag = null; });
      on(track, 'click', event => { if (cancelClick) { event.preventDefault(); event.stopPropagation(); cancelClick = false; } }, { capture: true });
      const resize = new ResizeObserver(requestUpdate);
      resize.observe(track);
      const visibility = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.intersectionRatio < .1) root.querySelectorAll('video').forEach(video => { if (!video.paused) video.pause(); });
      }), { threshold: [.1] });
      visibility.observe(root);
      root.classList.add('pm-carousel-ready');
      instances.set(root, () => { abort.abort(); resize.disconnect(); visibility.disconnect(); cancelAnimationFrame(frame); clearTimeout(clickTimer); root.classList.remove('pm-carousel-ready', 'pm-carousel-dragging'); root.querySelectorAll('video').forEach(v => v.pause()); });
      update();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => instances.forEach((destroy, root) => { if (event.target === root || event.target.contains(root)) { destroy(); instances.delete(root); } }));
  document.addEventListener('shopify:block:select', event => {
    const card = event.target.closest('[data-pm-carousel-track] > *');
    if (!card) return;
    const track = card.parentElement, box = card.getBoundingClientRect(), r = track.getBoundingClientRect();
    track.scrollBy({ left: getComputedStyle(track).direction === 'rtl' ? box.right - r.right : box.left - r.left, behavior: 'instant' });
  });
  document.addEventListener('visibilitychange', () => { if (document.hidden) document.querySelectorAll('[data-pm-carousel] video').forEach(v => v.pause()); });
})();
